import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken } from '../services/api';
import { PlusIcon, MagnifyingGlassIcon, EyeIcon, CheckCircleIcon, PencilIcon } from '@heroicons/react/24/outline';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import AddApplicationModal from './AddApplicationModal';
import UpdateApplicationStatusModal from './UpdateApplicationStatusModal';
import ApplicationDocuments from './ApplicationDocuments';
import ApplicationDetailsModal from './ApplicationDetailsModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
interface Application {

  applicationId: string;
  branchId: string;
  studentName: string;
  parentName: string;
  parentEmail: string;
  applyingForGrade: string;
  status: string;
  reviewStatus: string;
  admissionDecision: string | null;
  submittedAt: string;
}

interface ApplicationManagementProps {
  branchId?: string;
}

const ApplicationManagement: React.FC<ApplicationManagementProps> = ({ branchId = 'kolkata' }) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [archiveReason, setArchiveReason] = useState('');
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [docCounts, setDocCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchApplications();
    // Get user role from Firebase
    const auth = getAuth();
    const db = getFirestore();
    
    if (auth.currentUser) {
      getDoc(doc(db, 'users', auth.currentUser.uid)).then(docSnap => {
        if (docSnap.exists()) {
          setUserRole(docSnap.data().role || '');
        }
      }).catch(err => console.error('Error fetching user role:', err));
    }
  }, [branchId, showArchived]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      if (!token) {
        console.warn('⚠️ No authenticated user / token available');
        return;
      }
      
      const url = branchId === 'all'
        ? `${API_URL}/applications?showArchived=${showArchived}&limit=1000`
        : `${API_URL}/applications?branchId=${branchId}&showArchived=${showArchived}&limit=1000`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setApplications(response.data.data);
        // Fetch doc counts for all applications in parallel
        fetchDocCounts(response.data.data, token);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocCounts = async (apps: Application[], token: string | undefined) => {
    if (!apps.length || !token) return;
    const counts: Record<string, number> = {};
    await Promise.all(
      apps.map(async (app) => {
        try {
          const res = await axios.get(`${API_URL}/applications/${app.applicationId}/documents`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = res.data;
          counts[app.applicationId] = data.success ? (data.data?.length ?? 0) : 0;
        } catch {
          counts[app.applicationId] = 0;
        }
      })
    );
    setDocCounts(counts);
  };

  const statuses = [
    { value: 'all', label: 'All Status' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'enrolled', label: 'Admitted' }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'submitted': return 'status-new';
      case 'under_review': return 'status-contacted';
      case 'approved': return 'status-admitted';
      case 'rejected': return 'status-rejected';
      case 'admitted': return 'status-admitted';
      case 'enrolled': return 'status-admitted';
      default: return 'status-new';
    }
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    const matchesSearch =
      app.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.parentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.parentEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicationId.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = selectedStatus === 'all' || app.status === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  const handleArchive = async () => {
    if (!selectedApplication) return;
    try {
      const auth = getAuth();
      const token = await getAuthToken();
      
      const response = await fetch(
        `${API_URL}/applications/${selectedApplication.applicationId}/archive`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ reason: archiveReason })
        }
      );

      if (response.ok) {
        alert('Application archived successfully');
        setShowArchiveModal(false);
        setArchiveReason('');
        setSelectedApplication(null);
        fetchApplications();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to archive application');
      }
    } catch (error) {
      console.error('Error archiving application:', error);
      alert('Error archiving application');
    }
  };

  const handleUnarchive = async (app: Application) => {
    try {
      const auth = getAuth();
      const token = await getAuthToken();
      
      const response = await fetch(
        `${API_URL}/applications/${app.applicationId}/unarchive`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert('Application unarchived successfully');
        fetchApplications();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to unarchive application');
      }
    } catch (error) {
      console.error('Error unarchiving application:', error);
      alert('Error unarchiving application');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Application Management</h1>
          <p className="text-gray-600">Track and manage student applications</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Application
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="glass-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-semibold text-gray-900">{applications.length}</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-2xl font-semibold text-gray-900">
                {applications.filter(a => a.reviewStatus === 'pending').length}
              </p>
            </div>
            <div className="p-3 bg-rose-100 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-semibold text-gray-900">
                {applications.filter(a => a.status === 'approved').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="glass-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Enrolled</p>
              <p className="text-2xl font-semibold text-gray-900">
                {applications.filter(a => a.status === 'enrolled').length}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative md:col-span-2">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="input-field"
          >
            {statuses.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {/* Archive Toggle - Only for Superadmin */}
        {userRole === 'superadmin' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                showArchived
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
              }`}
            >
              {showArchived ? 'Showing Archived' : 'Show Archived'}
            </button>
          </div>
        )}
      </div>

      {/* Applications Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Application ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student / Parent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Docs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    Loading applications...
                  </td>
                </tr>
              ) : filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No applications found
                  </td>
                </tr>
              ) : (
                filteredApplications.map((app) => (
                  <tr key={app.applicationId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {app.applicationId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{app.studentName}</div>
                        <div className="text-sm text-gray-500">{app.parentName}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {app.applyingForGrade}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(app.status)}>
                        {app.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const count = docCounts[app.applicationId] ?? null;
                        if (count === null) return <span className="text-gray-400 text-xs">—</span>;
                        const colorClass = count === 0
                          ? 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          : count <= 2
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200';
                        return (
                          <button
                            onClick={() => { setSelectedApplication(app); setShowDocumentsModal(true); }}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${colorClass}`}
                            title="View / Upload Documents"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {count}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {app.submittedAt ? (() => {
                        try {
                          let date;
                          const submittedAt = app.submittedAt as any;
                          // Handle Firestore Timestamp object
                          if (typeof submittedAt === 'object' && submittedAt._seconds) {
                            date = new Date(submittedAt._seconds * 1000);
                          } 
                          // Handle ISO string
                          else if (typeof submittedAt === 'string') {
                            date = new Date(submittedAt);
                          }
                          
                          if (date && !isNaN(date.getTime())) {
                            return date.toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            });
                          }
                        } catch (e) {
                          console.error('Date parsing error:', e);
                        }
                        return 'N/A';
                      })() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedApplication(app);
                            setShowDetailsModal(true);
                          }}
                          className="text-gray-600 hover:text-gray-900"
                          title="View Application"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedApplication(app);
                            setShowUpdateModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Update Status"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        {userRole === 'superadmin' && (
                          showArchived ? (
                            <button
                              onClick={() => handleUnarchive(app)}
                              className="text-green-600 hover:text-green-900 text-xs font-medium"
                              title="Unarchive"
                            >
                              Restore
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedApplication(app);
                                  setShowArchiveModal(true);
                                }}
                                className="text-red-600 hover:text-red-900 text-xs font-medium ml-1"
                                title="Archive"
                              >
                                Archive
                              </button>
                            </>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
                </tbody>
              </table>
            </div>
          </div>

      {/* Add Application Modal */}
      <AddApplicationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onApplicationAdded={fetchApplications}
        branchId={branchId}
      />

      {/* Update Status Modal */}
      {selectedApplication && (
        <UpdateApplicationStatusModal
          isOpen={showUpdateModal}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedApplication(null);
          }}
          applicationId={selectedApplication.applicationId}
          currentStatus={selectedApplication.status}
          onStatusUpdated={fetchApplications}
        />
      )}

      {/* Documents Modal */}
      {selectedApplication && showDocumentsModal && (
        <ApplicationDocuments
          applicationId={selectedApplication.applicationId}
          studentName={selectedApplication.studentName}
          onClose={() => {
            setShowDocumentsModal(false);
            setSelectedApplication(null);
          }}
        />
      )}

      {/* Application Details Modal */}
      {selectedApplication && (
        <ApplicationDetailsModal
          isOpen={showDetailsModal}
          applicationId={selectedApplication.applicationId}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedApplication(null);
          }}
        />
      )}

      {/* Archive Modal */}
      {showArchiveModal && selectedApplication && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Archive Application</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to archive this application for <strong>{selectedApplication.studentName}</strong>?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for archiving (optional)
              </label>
              <textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="input-field"
                placeholder="e.g., Student withdrew, duplicate entry, etc."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowArchiveModal(false);
                  setArchiveReason('');
                }}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleArchive}
                className="btn-primary bg-red-600 hover:bg-red-700"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationManagement;
